import { Button } from "@/components/ui/button";
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/Page";
import type { Server } from "@/lib/api";
import type { Connector } from "@/lib/connectorCatalog";
import { useConnectForm } from "./useConnectForm";
import {
  AuthPicker,
  ConnectorBanner,
  KindFields,
  KindToggle,
  NameField,
  StdioArgs,
  StdioEnvTable,
} from "./sections";

// ConnectDialog is the modal form for reconnecting / editing an EXISTING
// integration, plus the playbook-author "add integration" popup. Fresh connects
// from the Integrations page use the spacious wizard (views/create/ConnectServer)
// instead. All three share one source of truth: useConnectForm + the section
// components.
export function ConnectDialog({
  reconnect,
  prefill,
  edit,
  onConnected,
  onError,
  onOAuth,
}: {
  reconnect: Server | null;
  prefill: Connector | null;
  // edit prefills an EXISTING integration's config for a reconnect-overwrite:
  // name + kind are locked; the kind's fields + auth method are prefilled
  // (secrets must be re-entered — they're never returned). Mutually exclusive
  // with reconnect/prefill.
  edit?: Server | null;
  onConnected: (serverName: string, building?: boolean) => void;
  onError: (msg: string) => void;
  // When set, an OAuth handoff runs in a popup (host stays on the page) instead
  // of a full-page redirect.
  onOAuth?: (authorizeUrl: string, serverName: string, popup: Window | null) => void;
}) {
  const form = useConnectForm({ reconnect, prefill, edit, onConnected, onError, onOAuth });

  const titleText = reconnect
    ? `Reconnect ${reconnect.name}`
    : edit
      ? `Edit ${edit.name}`
      : prefill
        ? `Connect ${prefill.name}`
        : "Connect an integration";
  const descText = reconnect
    ? "Sign in again to reconnect this integration."
    : edit
      ? "Update how this integration is configured."
      : "Connect a service your agents can use.";

  return (
    <DialogContent className="max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{titleText}</DialogTitle>
        <DialogDescription>{descText}</DialogDescription>
      </DialogHeader>
      <form onSubmit={(e) => void form.submit(e)} className="space-y-4">
        <ConnectorBanner form={form} />
        {form.freshConnect && <KindToggle form={form} />}
        <NameField form={form} />
        <KindFields form={form} />
        {form.isStdio ? (
          <>
            <div className="space-y-1.5">
              <Label>Environment variables</Label>
              <StdioEnvTable form={form} />
            </div>
            <StdioArgs form={form} />
          </>
        ) : (
          <AuthPicker form={form} />
        )}
        {form.err && <p className="text-sm font-medium text-destructive">{form.err}</p>}
        <DialogFooter>
          <Button type="submit" disabled={form.busy || !form.submittable}>
            {form.busy && <Spinner />} {form.submitLabel}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
